import * as React from 'react';
import $ from 'jquery';
import { debounce } from 'perfect-debounce';
import { useInView, InView } from "react-intersection-observer";
import classNames from 'classnames'
import { accentUtils, format, from, t } from '../../services/HelperService';
import { configFrom } from '../../services/ProductService';
import { ConfiguratorContext, ConfiguratorContextProvider } from './ConfigContextProvider';


import { ErrorWarningIndicator, getSizeRequirements, isHiddenWhenDisabled, isOptionVisible, isReadOnly, ProductValidationBanner, saveOrderLineOptions} from './ProductConfiguratorShared';
import { OptionValue, SizeCtrl } from './VerticalConfiguratorCtrl';



export const TableConfiguratorCtrl = React.memo(props => {

    const table = React.useRef();            
    const lineRefs = React.useRef({});
    const [hidden, setHidden] = React.useState({});


    React.useImperativeHandle(
        props.methods,
        () => ({
            save: async () => {
                if (accentUtils.isNull(lineRefs.current)) return;

                const keys = Object.keys(lineRefs.current);

                for (var i = 0; i < keys.length; i++) {

                    const k = keys[i];

                    if (!lineRefs.current[k]) continue;

                    await lineRefs.current[k].save();
                }

                
            },
            cleanUp: () => {
                if (accentUtils.isNull(lineRefs.current)) return;

                const keys = Object.keys(lineRefs.current);

                for (var i = 0; i < keys.length; i++) {

                    const k = keys[i];

                    if (!lineRefs.current[k]) continue;

                    lineRefs.current[k].cleanUp();
                }

            },
            notifyQtyChanged: (lineID) => {

                if (accentUtils.isNull(lineRefs.current)) return;
                if (accentUtils.isNull(lineRefs.current[lineID])) return;

                lineRefs.current[lineID].notifyQtyChanged();
            },
            notifyLocationChanged: (lineID) => {

                if (accentUtils.isNull(lineRefs.current)) return;
                if (accentUtils.isNull(lineRefs.current[lineID])) return;

                lineRefs.current[lineID].notifyLocationChanged();

            }
        }),
        [lineRefs]
    );

    const productSize = props.config.GetSize();

    
    const size = getSizeRequirements(productSize);
    const hasSize = size.requireHeight || size.requireWidth || size.requireMultiHeight || size.requireMultiWidth;

    const groups = props.config.GetProductOptionGroups().toArray().filter(g => g.Active && !g.MultiProductOption);


    const onHiddenChanged = React.useCallback(e => {

        setHidden(h => ({ ...h, [e.orderLine.ID] : e.ids }));

    }, [setHidden]);

    React.useEffect(() => {
        console.log("TableConfiguratorCtrl Mount");
    }, []);

    let groupHeaders = [];
    let optionHeaders = [];        


    // A column only hides when every reporting line hides it (intersection). Lines with the
    // option visible keep the column alive; lines that hide it render a blank cell instead.
    let allHidden = {};

    let hiddenIntersection = null;

    Object.keys(hidden).forEach(lineID => {

        const lineOptionIDs = new Set(hidden[lineID].split(",").filter(id => id !== ""));

        if (hiddenIntersection === null) {
            hiddenIntersection = lineOptionIDs;
        } else {
            hiddenIntersection = new Set([...hiddenIntersection].filter(id => lineOptionIDs.has(id)));
        }
    });

    if (hiddenIntersection !== null) {
        hiddenIntersection.forEach(id => { allHidden[id] = true; });
    }

    groups.map(g => {

        const options = configFrom(props.config.GetProductOptions(g.ID)).where(o => o.Active && !allHidden[o.ID.toString().toUpperCase()]).toArray();
        let groupUsed = false;
        options.map(o => {

            optionHeaders.push(<th className="option-header" key={`p${o.ID}`} title={o.Description}><div>{o.Description}</div></th>);

            groupUsed = true;

        });

        if (groupUsed) {
            groupHeaders.push(<th colSpan={options.length} key={`p${g.ID}`} className="group-header">{g.Description}</th>);
        }

    });


    const keepLoaded = props.lines.length <= 30 || true;



    const lines =  props.lines.map(l => <TableLineRow
        defaultConfig={props.config}
        methods={r => lineRefs.current[l.ID] = r}
        key={`${l.viewChangeCount}_${l.ID}`}
        line={l}
        getPreColumns={props.getPreColumns}
        getPostColumns={props.getPostColumns}
        businessUnitID={props.businessUnitID}
        companyGuid={props.companyGuid}
        companyID={props.companyID}
        mpResellerKeys={props.mpResellerKeys}
        isQuote={props.isQuote}
        ignoreSizes={props.ignoreSizes}
        isLineReadOnly={props.isLineReadOnly}
        isReadOnly={props.isReadOnly}
        onChange={props.onChange}
        notifyDirty={props.notifyDirty}
        onLoaded={props.onLoaded}
        onSave={props.onSave}
        configFilter={props.configFilter}
        onHiddenChanged={onHiddenChanged}
        allHidden={allHidden}
        keepLoaded={keepLoaded}
        getMultiConfigManagerAsync={props.getMultiConfigManagerAsync}
        onProductSourceChanged={props.onProductSourceChanged}
        getAllowSourceScript={props.getAllowSourceScript }
        />);
    

    const className = classNames("config-table", props.className);

    return <div className={className} ref={table}>
        <table >
            <tbody>
                <tr>
                    {props.preGroupHeaders}
                    {hasSize && <th key="size" colSpan="2" >Size</th>}
                    {groupHeaders}
                    {props.postGroupHeaders}
                </tr>
                <tr>
                    {props.preOptionHeaders}
                    {(size.requireWidth || size.requireMultiWidth) && <th key="width" >{t("application_strings.views.multiLineEdit.width")}</th>}
                    {(size.requireHeight || size.requireMultiHeight) && <th key="drop" >{t("application_strings.views.multiLineEdit.drop")}</th>}
                    {optionHeaders}
                    {props.postOptionHeaders}
                </tr>
                {lines}
            </tbody>
        </table>
    </div>;

});



const TableLineRow = React.memo(props => {


    const getMultiConfigManagerAsync = props.getMultiConfigManagerAsync;
    
    const provider = React.useRef();


    const isMultiConfigLine = !accentUtils.isEmpty(props.line.MultiProductID);

    const [show, setShow] = React.useState(props.line.ID < 0);

    const [multiConfigManager, setMultiConfigManager] = React.useState(null);

    const { ref, inView } = useInView({
        threshold: 0,    
        delay: 100,
        skip: show
    });


    const updateShow = React.useCallback((e) => {

        if (!e.show && e.inView) {
            setShow(true);
            getMultiConfigManagerAsync(props.line, props).then(m => setMultiConfigManager(m));
        }

    }, [setShow, setMultiConfigManager, getMultiConfigManagerAsync, props]);

    const showDebounced = React.useRef(debounce(updateShow, 1000, { trailing: false }));


    React.useImperativeHandle(
        props.methods,
        () => ({
            save: async () => {

                if (provider.current) {
                    await provider.current.save();
                }
            },
            cleanUp: () => {

                if (provider.current) {
                    provider.current.cleanUp();
                }
            },
            notifyQtyChanged: () => {

                if (provider.current) {
                    provider.current.notifyQtyChanged();
                }                
            },
            notifyLocationChanged: () => {
                if (provider.current) {
                    provider.current.notifyLocationChanged();
                }                
            },
            
        }),
        [provider]
    );

    const onProductSourceChanged = React.useCallback(e => {

        if (props.onProductSourceChanged) {
            props.onProductSourceChanged(e, props.line);
        }
    }, [props]);

    const getAllowSourceScript = React.useCallback(e => {
        return props.getAllowSourceScript(props.line);
        
    }, [props]);

    

    const onChange = React.useCallback(e => {
        props.onChange();

        provider.current.save();

    }, [props, provider]);


    const onSaveOLOs = React.useCallback(e => {
        saveOrderLineOptions(props.line, e);
    }, [props]);

    const onInProgressChanged = e => {

        console.log(`TableLineRow onInProgressChanged - ${e}`);

        if (e)
            $(`#tr_${props.line.ID}`).addClass("change-in-progress");
        else
            $(`#tr_${props.line.ID}`).removeClass("change-in-progress");
    };

    React.useEffect(() => {
        console.log("TableLineRow Mount");
    }, []);


    React.useEffect(() => {

        if (!show) {
            showDebounced.current({ show, inView});
        }

    }, [show, inView, showDebounced]);


    React.useEffect(() => {

        if (show && isMultiConfigLine && accentUtils.isNull(multiConfigManager)) {

            getMultiConfigManagerAsync(props.line, props).then(m => setMultiConfigManager(m));

        }

    }, []);



    const lineIsReadOnly = props.isReadOnly || (props.isLineReadOnly && props.isLineReadOnly(props.line));

    const preColumns = props.getPreColumns ? props.getPreColumns(props.line, lineIsReadOnly) : null;
    const postColumns = props.getPostColumns ? props.getPostColumns(props.line, lineIsReadOnly) : null;



    if (!show || (isMultiConfigLine && accentUtils.isNull(multiConfigManager))) {        
        return <TableLineRowNotVisible
            ref={ref}
            defaultConfig={props.defaultConfig}
            line={props.line}
            getPreColumns={props.getPreColumns}
            getPostColumns={props.getPostColumns}
            allHidden={props.allHidden}
            table={props.table}
        />;
    } 


    return <ConfiguratorContextProvider
                methods={provider}
                productID={props.line.ProductID}
                orderLine={props.line}
                orderLineOptions={JSON.parse(props.line.OrderLineOptionsData)}
                businessUnitID={props.businessUnitID}
                companyGuid={props.companyGuid}
                companyID={props.companyID}
                mpResellerKeys={props.mpResellerKeys}
                isQuote={props.isQuote}
                ignoreSizes={props.ignoreSizes}
                isReadOnly={lineIsReadOnly}
                onValuedChanged={onChange}
                onValuedSize={onChange}
                notifyDirty={props.notifyDirty}
                onProductSourceChanged={props.onProductSourceChanged ?  onProductSourceChanged : undefined}
                allowProductSourceScript={getAllowSourceScript()}
                onLoaded={props.onLoaded}
                onSave={onSaveOLOs}
                filter={props.configFilter}
                onInProgressChanged={onInProgressChanged}
                multiConfigManager={multiConfigManager}
                loader={() => (
                    <tr
                        key={`tr_${props.line.ID}`}
                        id={`tr_${props.line.ID}`}
                        ref={ref}
                    >
                        {preColumns}
                        <CellLoader line={props.line} defaultConfig={props.defaultConfig} allHidden={props.allHidden} />
                        {postColumns}
                    </tr>
                )}
            >
                <TableLineConfigured
                    rowRef={ref}
                    lineId={props.line.ID}
                    preColumns={preColumns}
                    postColumns={postColumns}
                    defaultConfig={props.defaultConfig}
                    onHiddenChanged={props.onHiddenChanged}
                    allHidden={props.allHidden}
                />
            </ConfiguratorContextProvider>;
        
});


const TableLineConfigured = props => {

    const ctx = React.useContext(ConfiguratorContext);

    React.useEffect(() => {
        ctx.config.ValidateOptions(true);
    }, []);

    return (
        <>
            <tr
                key={`tr_${props.lineId}`}
                id={`tr_${props.lineId}`}
                ref={props.rowRef}
            >
                {props.preColumns}
                <TableSizeCells defaultConfig={props.defaultConfig} />
                <TableOptionCells defaultConfig={props.defaultConfig} onHiddenChanged={props.onHiddenChanged} allHidden={props.allHidden} />
                {props.postColumns}
            </tr>
            <ProductValidationBanner asTableRow />
        </>
    );
};





const TableOptionCells = props => {


    const ctx = React.useContext(ConfiguratorContext);

    // Iterate the same static structure the table headers use, so per-line visibility can never
    // misalign the cells against the header columns.
    // groupByOptionID keeps each option's owning group so the line can apply the
    // runtime's group-level show/hide (IsGroupVisibleForOrder) the same way the
    // vertical configurator's Group does.
    const { structuralOptions, groupByOptionID } = React.useMemo(() => {

        const options = [];
        const groups = {};

        configFrom(props.defaultConfig.GetProductOptionGroups())
            .where(g => g.Active && !g.MultiProductOption)
            .select(g => {

                configFrom(props.defaultConfig.GetProductOptions(g.ID))
                    .where(o => o.Active)
                    .select(o => {
                        options.push(o);
                        groups[o.ID.toString().toUpperCase()] = g;
                    }).toArray();

            }).toArray();

        return { structuralOptions: options, groupByOptionID: groups };

    }, [props.defaultConfig]);

    // Hidden for this line = group hidden by the runtime (e.g. the SWF synthesized
    // pricing groups), filter-hidden, or runtime-hidden (locked with HideIfDisabled
    // set). The table-level intersection then drops the column — and the group
    // header once none of its columns remain — when every line hides it.
    const computeHiddenIDs = React.useCallback(() => {

        const hidden = [];
        const groupVisible = {};

        structuralOptions.forEach(o => {

            const optionModel = ctx.config.GetOptionModel(o.ID);

            if (accentUtils.isNull(optionModel)) return;

            const optionID = o.ID.toString().toUpperCase();
            const group = groupByOptionID[optionID];
            const groupKey = group?.ID?.toString().toUpperCase();

            if (groupKey && groupVisible[groupKey] === undefined) {
                groupVisible[groupKey] = ctx.config.IsGroupVisibleForOrder(group);
            }

            if ((groupKey && !groupVisible[groupKey]) || !isOptionVisible(optionModel, ctx.filter) || isHiddenWhenDisabled(o, optionModel)) {
                hidden.push(optionID);
            }
        });

        return hidden.join(",");

    }, [structuralOptions, groupByOptionID, ctx]);

    const [hiddenIDs, setHiddenIDs] = React.useState(() => computeHiddenIDs());

    // Recompute after each change/size event (the runtime has already re-evaluated by the time
    // these publish); only store when the hidden set actually changes.
    React.useEffect(() => {

        const registerKey = `TableOptionCells_${ctx.productID}_${ctx.orderLine.ID}`;

        const onRecompute = () => {
            const ids = computeHiddenIDs();
            setHiddenIDs(prev => (prev === ids ? prev : ids));
        };

        ctx.config.Subscribe(window.InsyteConfig.InsyteConfigEvents.ChangeOccurred, onRecompute, registerKey);
        ctx.config.Subscribe(window.InsyteConfig.InsyteConfigEvents.SizeChangeOccurred, onRecompute, registerKey);

        onRecompute();

        return () => {
            ctx.config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.ChangeOccurred, registerKey);
            ctx.config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.SizeChangeOccurred, registerKey);
        };

    }, [ctx.config, computeHiddenIDs, ctx.productID, ctx.orderLine.ID]);

    React.useEffect(() => {
        props.onHiddenChanged({
            orderLine: ctx.orderLine,
            ids: hiddenIDs
        });
    }, [hiddenIDs]);

    const hiddenForLine = React.useMemo(() => {
        return hiddenIDs.split(",").filter(id => id !== "").reduce((p, v) => ({ ...p, [v]: true }), {});
    }, [hiddenIDs]);

    const cells = structuralOptions
        .filter(o => !props.allHidden[o.ID.toString().toUpperCase()])
        .map(o => <TableOptionCell
            key={`key_${o.ID}`}
            option={o}
            hiddenForLine={hiddenForLine[o.ID.toString().toUpperCase()] === true} />);

    return <React.Fragment>
        { cells}
    </React.Fragment>;
};


const TableSizeCells = props => {

    const productSize = props.defaultConfig.GetSize();

    if (productSize == null) {
        return null;
    }

    const size = getSizeRequirements(productSize);

    const width = size.requireWidth ? <td><SizeCtrl width small allowedValuesField="AllowedWidthList" productSize={productSize} requireMulti={size.requireMultiWidth || size.requireMultiHeight} /></td> : null;
    const height = size.requireHeight ? <td><SizeCtrl small allowedValuesField="AllowedHeightList" productSize={productSize} requireMulti={size.requireMultiWidth || size.requireMultiHeight} /></td> : null;



    return <React.Fragment>
        {width}
        { height}
    </React.Fragment>;
};



const TableOptionCell = props => {


    const ctx = React.useContext(ConfiguratorContext);
    const optionModel = ctx.config.GetOptionModel(props.option.ID);

    const itemID = `opt-${props.option?.ID?.toString()}`;

    // Blank cell: other lines use this column, but this line's runtime hides the option
    // (or this line has no model for it), so there is genuinely no value to show.
    if (props.hiddenForLine || accentUtils.isNull(optionModel)) {
        return <td key={itemID} id={itemID} className="config-option-cell option-hidden"></td>;
    }

    const readOnly = isReadOnly(optionModel);

    return <td key={itemID} id={itemID} className={classNames("config-option-cell", { "readonly": readOnly })}>
        <div className="config-option-cell-inner">
            <ErrorWarningIndicator optionID={props.option?.ID?.toString()} />
            <OptionValue optionID={props.option?.ID?.toString()} displayType={optionModel.DisplayType} customDisplayType={props.option.CustomDisplayType} small />
        </div>
    </td>;
};

const TableSizeCellsNotVisible = props => {

    const productSize = props.defaultConfig.GetSize();

    if (productSize == null) {
        return null;
    }

    const size = getSizeRequirements(productSize);

    const width = size.requireWidth ? <td>{format.formatLength(props.line.Width, props.line.ProductBaseUnit, props.line.ProductBaseUnitFormat)}</td> : null;
    const height = size.requireHeight ? <td>{format.formatLength(props.line.Drop, props.line.ProductBaseUnit, props.line.ProductBaseUnitFormat)}</td> : null;



    return <React.Fragment>
        {width}
        {height}
    </React.Fragment>;
};


const TableLineRowNotVisible = React.memo(React.forwardRef((props, ref) => {

   
    React.useImperativeHandle(
        props.methods,
        () => ({
            save: () => {
            }
        }),
        [props]
    );

   

    const preColumns = props.getPreColumns ? props.getPreColumns(props.line) : null;
    const postColumns = props.getPostColumns ? props.getPostColumns(props.line) : null;


    return <tr ref={ref} key={`tr_${props.line.ID}`} id={`tr_${props.line.ID}`}>
        {preColumns}
        <TableSizeCellsNotVisible defaultConfig={props.defaultConfig} line={props.line} />
        <TableOptionCellsNotVisible defaultConfig={props.defaultConfig} allHidden={props.allHidden} />
        {postColumns}
    </tr>;
}));


const CellLoader = props => {

    return <React.Fragment>
        <TableSizeCellsNotVisible defaultConfig={props.defaultConfig} line={props.line} />
        <TableOptionCellsNotVisible defaultConfig={props.defaultConfig} allHidden={props.allHidden} />
    </React.Fragment>;
};

const TableOptionCellsNotVisible = props => {

    const cells = [];

    const groups = configFrom(props.defaultConfig.GetProductOptionGroups())
        .where(g => g.Active && !g.MultiProductOption)
        .select(g => {

            configFrom(props.defaultConfig.GetProductOptions(g.ID))
            .where(o => o.Active && !props.allHidden[o.ID.toString().toUpperCase()])
                .select(o => {
                    const optionID = o.ID.toString().toUpperCase();
                    cells.push(<TableOptionCellNotVisible key={`key_${o.ID}`} option={o} />);
               
                }).toArray();

        }).toArray();

    return <React.Fragment>
        {cells}
    </React.Fragment>;
};




const TableOptionCellNotVisible = props => {
    const itemID = `opt-${props.option?.ID?.toString()}`;


    return <td id={ itemID} className={`config-option-cell`}>
        .........
    </td>;
};
